// Doctor Image Service - Handles uploading and syncing doctor images across devices
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../config/firebase';

const storage = getStorage(app);

export class DoctorImageService {
  /**
   * Upload doctor image to Firebase Storage
   * @param {File} imageFile - Image file from input
   * @param {string} doctorId - Doctor ID for organizing storage
   * @returns {Promise<string>} - Cloud download URL
   */
  static async uploadDoctorImage(imageFile: File, doctorId: string): Promise<string> {
    try {
      console.log('📤 DoctorImageService: Starting upload...', { 
        fileName: imageFile.name, 
        fileSize: imageFile.size,
        doctorId 
      });
      
      if (!imageFile || !doctorId) {
        throw new Error('Image file and Doctor ID are required');
      }
      
      // Create storage reference with timestamp for uniqueness
      const timestamp = Date.now();
      const imagePath = `doctor-images/${doctorId}/profile-${timestamp}.jpg`;
      const imageRef = ref(storage, imagePath);
      
      console.log('☁️ Uploading to Firebase Storage:', imagePath);
      
      // Upload to Firebase Storage
      const snapshot = await uploadBytes(imageRef, imageFile);
      console.log('✅ Upload completed, getting download URL...');
      
      // Get public download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Doctor image uploaded successfully:', downloadURL);
      
      return downloadURL;
    } catch (error: any) {
      console.error('❌ DoctorImageService upload failed:', error);
      
      // Provide more specific error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('You are not authorized to upload images. Please log in again.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('An unknown error occurred during upload.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  }
  
  /**
   * Check if an image URL is a cloud URL (Firebase Storage) or local/base64
   * @param {string} imageUri - Image URI to check
   * @returns {boolean} - True if cloud URL, false if local/base64
   */
  static isCloudImageUrl(imageUri: string | undefined | null): boolean {
    if (!imageUri) return false;
    
    return (
      imageUri.includes('firebasestorage.googleapis.com') ||
      imageUri.includes('storage.googleapis.com') ||
      imageUri.includes('.firebasestorage.app')
    );
  }
  
  /**
   * Get optimal image URI - prefer cloud over base64
   * @param {Object} doctorData - Doctor data object
   * @returns {string|null} - Best available image URI
   */
  static getBestImageUri(doctorData: any): string | null {
    const avatar = doctorData?.avatar;
    
    if (this.isCloudImageUrl(avatar)) {
      console.log('☁️ Using cloud doctor image:', avatar);
      return avatar;
    }
    
    // If we have a base64 or UI avatars URL, return it but log warning
    if (avatar) {
      if (avatar.startsWith('data:')) {
        console.log('📱 Using base64 doctor image (will not sync across devices)');
      } else {
        console.log('🔗 Using generated avatar URL:', avatar);
      }
      return avatar;
    }
    
    console.log('ℹ️ No doctor image available');
    return null;
  }
}

export default DoctorImageService;
